/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { setStructuredData, clearStructuredData } from "@/lib/structuredData";
import { Check, X, Minus, Trophy } from "lucide-react";

const FAQ_KEYS = [1, 2, 3, 4];

const ROW_KEYS = [
  { key: "tracking", aurora: true, flamme: false, paired: "partial", maia: false, chatgpt: false },
  { key: "memory", aurora: true, flamme: false, paired: false, maia: true, chatgpt: "partial" },
  { key: "faith", aurora: true, flamme: false, paired: false, maia: false, chatgpt: false },
  { key: "shared", aurora: true, flamme: true, paired: true, maia: false, chatgpt: false },
  { key: "patterns", aurora: true, flamme: false, paired: false, maia: false, chatgpt: false },
  { key: "local", aurora: true, flamme: "partial", paired: "partial", maia: false, chatgpt: false },
  { key: "repair", aurora: true, flamme: false, paired: "partial", maia: true, chatgpt: "partial" },
  { key: "journeys", aurora: true, flamme: true, paired: true, maia: false, chatgpt: false },
  { key: "chapters", aurora: true, flamme: false, paired: false, maia: false, chatgpt: false },
  { key: "export", aurora: true, flamme: false, paired: false, maia: false, chatgpt: false },
];

const Cell = ({ v }) =>
  v === true ? <Check size={18} className="text-[var(--aurora-gold)]" />
  : v === "partial" ? <Minus size={18} className="text-white/40" />
  : <X size={16} className="text-white/25" />;

const ComparisonPage = () => {
  const { t } = useTranslation("site");
  const tk = (k, fb) => t(`comparison.${k}`, fb);

  // FAQPage structured data for rich results on this high-intent page.
  useEffect(() => {
    const mainEntity = FAQ_KEYS
      .map((n) => ({ q: t(`comparison.faq.q${n}`, ""), a: t(`comparison.faq.a${n}`, "") }))
      .filter((item) => item.q && item.a)
      .map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      }));
    if (mainEntity.length) {
      setStructuredData("comparison-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity,
      });
    }
    return () => clearStructuredData("comparison-faq");
  }, [t]);

  return (
    <>
      <section className="relative pb-12">
        <AuroraBackground variant="soft" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-8 lg:pt-12">
          <Reveal>
            <p className="eyebrow">{tk("kicker")}</p>
            <h1 className="mt-3 font-serif text-[2.4rem] sm:text-5xl lg:text-[4rem] tracking-tight font-light leading-[1.05] max-w-4xl">
              {tk("title1")}{" "}<em className="italic aurora-shimmer-text">{tk("title2")}</em>{" "}{tk("title3")}
            </h1>
            <p className="mt-6 max-w-2xl text-white/72 text-lg font-light">{tk("subtitle")}</p>
            <div className="mt-8">
              <StoreBadgeGroup startLabel={tk("cta")} />
              <p className="mt-3 text-xs text-white/45 tracking-wide">{t("home.freeBadge")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="aurora-glass overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-white/8">
                    <th className="p-5 font-sans font-medium text-white/55 uppercase tracking-[0.15em] text-xs">
                      {tk("feature")}
                    </th>
                    <th className="p-5 font-serif text-base text-[var(--aurora-gold)]">withAurora</th>
                    <th className="p-5 font-serif text-base text-white/55">Flamme</th>
                    <th className="p-5 font-serif text-base text-white/55">Paired</th>
                    <th className="p-5 font-serif text-base text-white/55">Maia</th>
                    <th className="p-5 font-serif text-base text-white/55">ChatGPT</th>
                  </tr>
                </thead>
                <tbody>
                  {ROW_KEYS.map((r) => (
                    <tr key={r.key} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-5 text-white/80 font-light">{tk(`rows.${r.key}`)}</td>
                      <td className="p-5 bg-[var(--aurora-journeys)]/40 border-l border-r border-[var(--aurora-gold)]/20"><Cell v={r.aurora} /></td>
                      <td className="p-5"><Cell v={r.flamme} /></td>
                      <td className="p-5"><Cell v={r.paired} /></td>
                      <td className="p-5"><Cell v={r.maia} /></td>
                      <td className="p-5"><Cell v={r.chatgpt} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-white/45 flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-1"><Check size={12} className="text-[var(--aurora-gold)]" /> {tk("legendBuilt")}</span>
              <span className="inline-flex items-center gap-1"><Minus size={12} className="text-white/40" /> {tk("legendPartial")}</span>
              <span className="inline-flex items-center gap-1"><X size={12} className="text-white/25" /> {tk("legendNo")}</span>
            </p>
          </Reveal>

          {/* Verdict strip */}
          <Reveal delay={120}>
            <div className="mt-10 aurora-glass p-8 sm:p-10 border border-[var(--aurora-gold)]/25 text-center">
              <span className="inline-flex items-center gap-2 text-[var(--aurora-gold)] text-[0.7rem] uppercase tracking-[0.25em]">
                <Trophy size={14} /> {tk("verdictKicker", "The verdict")}
              </span>
              <p className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight font-light">
                {tk("verdictTitle", "Most apps do one piece. Aurora runs the whole loop.")}
              </p>
              <p className="mt-4 text-white/70 font-light max-w-2xl mx-auto">
                {tk("verdictBody", "Prompt apps spark conversation. Companion AIs listen. Only Aurora tracks your real patterns, coaches the hard moments, and keeps memory across seasons — privately.")}
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <StoreBadgeGroup startLabel={tk("closingCta")} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 grid md:grid-cols-3 gap-5">
          {[
            { title: tk("vsFlammeT"), body: tk("vsFlammeB") },
            { title: tk("vsPairedT"), body: tk("vsPairedB") },
            { title: tk("vsGptT"), body: tk("vsGptB") },
          ].map((c) => (
            <Reveal key={c.title}>
              <div className="aurora-card p-7 bg-[var(--aurora-coach)]/70">
                <p className="font-serif italic text-[var(--aurora-gold)] text-lg">{c.title}</p>
                <p className="mt-3 text-white/75 font-light leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Comparison FAQ — mirrors the FAQPage structured data above */}
      <section className="relative py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light">
              {tk("faq.title", "Questions people ask before choosing")}
            </h2>
          </Reveal>
          <div className="mt-8 space-y-4">
            {FAQ_KEYS.map((n, i) => (
              <Reveal key={n} delay={i * 60}>
                <div className="aurora-glass p-6">
                  <h3 className="font-serif text-xl text-white/95">{tk(`faq.q${n}`, "")}</h3>
                  <p className="mt-2 text-white/70 font-light leading-relaxed">{tk(`faq.a${n}`, "")}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light leading-tight">
              {tk("closingTitle1")}{" "}<em className="italic aurora-shimmer-text">{tk("closingTitle2")}</em>
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={tk("closingCta")} />
            </div>
            <p className="mt-4 text-xs text-white/45 tracking-wide">{t("common.freeFooter")}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default ComparisonPage;
